import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AudioUpload } from "@/components/AudioUpload";

const empty = { title: "", topic: "", summary: "", content: "", image: "", audio_url: "", related_subcategory: "" };

export const LearnManager = ({ onChange }) => {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/learn").then((r) => setEntries(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openEditor = (entry) => {
    setEditing(entry);
    setForm(entry ? { title: entry.title, topic: entry.topic, summary: entry.summary, content: entry.content, image: entry.image || "", audio_url: entry.audio_url || "", related_subcategory: entry.related_subcategory || "" } : empty);
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      if (editing) await api.put(`/admin/learn/${editing.id}`, form);
      else await api.post("/learn", form);
      toast.success("Saved");
      setOpen(false); load(); onChange?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry permanently?")) return;
    try { await api.delete(`/admin/learn/${id}`); toast.success("Entry deleted"); load(); onChange?.(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-lg md:text-2xl text-foreground">Learn Library ({entries.length})</h2>
        <Button size="sm" data-testid="add-learn-btn" className="rounded-full" onClick={() => openEditor(null)}>
          <Plus size={14} className="mr-1" /> New entry
        </Button>
      </div>

      <div className="space-y-2" data-testid="admin-learn-list">
        {entries.map((e) => (
          <div key={e.id} className="silk-card rounded-lg px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-foreground truncate">{e.title}</p>
              <p className="text-xs text-muted-foreground">{e.topic} · {e.author_name}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Badge variant="outline" className="font-normal text-xs">{e.status}</Badge>
              <button data-testid={`edit-learn-${e.id}`} onClick={() => openEditor(e)} className="text-muted-foreground hover:text-foreground"><Pencil size={15} /></button>
              <button data-testid={`delete-learn-${e.id}`} onClick={() => remove(e.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit entry" : "New Learn entry"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input data-testid="learn-editor-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <Input data-testid="learn-editor-topic" placeholder="Topic" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} />
            </div>
            <Input data-testid="learn-editor-summary" placeholder="One-line summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
            <Textarea data-testid="learn-editor-content" rows={10} placeholder="Content… (blank line between paragraphs)" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
            <Input placeholder="Image URL (optional)" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
            <div>
              <p className="text-xs text-muted-foreground mb-2">Audio (optional) — upload or record</p>
              <AudioUpload value={form.audio_url} onChange={(url) => setForm({ ...form, audio_url: url })} />
            </div>
            <Input data-testid="learn-editor-related" placeholder="Related specialty for cross-link (e.g. Reiki)" value={form.related_subcategory} onChange={(e) => setForm({ ...form, related_subcategory: e.target.value })} />
            <Button data-testid="learn-editor-save" onClick={save} disabled={busy || !form.title || !form.content} className="rounded-full px-6">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
